import { useState } from "react";
import { mormSigner, AgentDelegationResult, KeypairResult } from "../sdk";
import { useAccount, useSignTypedData } from "wagmi";

// Supported wallet types
export type SupportedWallet = "metamask" | "phantom" | "onekey";

// Type declaration for Ethereum provider
interface EthereumProvider {
  request(args: { method: "eth_requestAccounts" }): Promise<string[]>;
  request(args: {
    method: "eth_signTypedData_v4";
    params: [string, string];
  }): Promise<string>;
  isMetaMask?: boolean;
  isPhantom?: boolean;
  isOneKey?: boolean;
  providers?: EthereumProvider[];
}

// Type declaration for Phantom wallet
interface PhantomProvider {
  ethereum?: EthereumProvider;
  isPhantom?: boolean;
}

// Type declaration for OneKey wallet
interface OneKeyProvider {
  ethereum?: EthereumProvider;
  isOneKey?: boolean;
}

declare global {
  interface Window {
    ethereum?: EthereumProvider;
    phantom?: PhantomProvider;
    $onekey?: OneKeyProvider;
  }
}

/**
 * Detects which supported wallet is available
 * Returns 'metamask', 'phantom', 'onekey', or null
 */
export function detectSupportedWallet(): SupportedWallet | null {
  if (typeof window === "undefined") return null;

  // Check for Phantom
  if (window.phantom?.ethereum || window.ethereum?.isPhantom) {
    return "phantom";
  }

  // Check for OneKey
  if (window.$onekey?.ethereum || window.ethereum?.isOneKey) {
    return "onekey";
  }

  // Check for MetaMask (must be last as it's the most common)
  if (window.ethereum?.isMetaMask) {
    return "metamask";
  }

  // If ethereum exists but no specific wallet detected, assume MetaMask
  if (window.ethereum) {
    return "metamask";
  }

  return null;
}

/**
 * Validates if the connected wallet is one of the supported wallets
 */
export function isSupportedWallet(
  wallet: string | null,
): wallet is SupportedWallet {
  return wallet === "metamask" || wallet === "phantom" || wallet === "onekey";
}

/**
 * Custom hook for managing agent delegation operations
 *
 * Handles the complete agent delegation workflow:
 * - Generating owner address from mnemonic
 * - Generating agent keypair
 * - Preparing agent sign (EIP-712 typed data)
 * - Signing agent approval
 *
 * @param initialOwnerMnemonic - Initial owner mnemonic phrase (default: standard test mnemonic)
 * @param initialExpiresInDays - Initial expiration days (default: 180)
 * @param initialPermissions - Initial permissions string (default: 'clob::submit_order,clob::cancel_order')
 * @param initialAgentName - Initial agent name (default: 'TradingBot')
 * @returns Object containing:
 *   - ownerMnemonic: Current owner mnemonic phrase
 *   - setOwnerMnemonic: Function to update owner mnemonic
 *   - ownerAddress: Generated owner address
 *   - setOwnerAddress: Function to update owner address
 *   - agentAddress: Generated agent address
 *   - setAgentAddress: Function to update agent address
 *   - agentPrivateKey: Generated agent private key
 *   - setAgentPrivateKey: Function to update agent private key
 *   - expiresInDays: Expiration days for delegation
 *   - setExpiresInDays: Function to update expiration days
 *   - permissions: Permissions string (comma-separated)
 *   - setPermissions: Function to update permissions
 *   - agentName: Agent name
 *   - setAgentName: Function to update agent name
 *   - nonce: Nonce for delegation
 *   - setNonce: Function to update nonce
 *   - result: Delegation result or error
 *   - loading: Boolean indicating if operation is in progress
 *   - generateOwnerAddress: Function to generate owner address from mnemonic
 *   - generateAgentKeypair: Function to generate agent keypair
 *   - prepareAgentSign: Function to prepare agent sign (EIP-712 typed data)
 *   - signAgentApproval: Function to sign agent approval
 *   - isReady: Boolean indicating if mormSigner is ready
 */
export function useAgentDelegation(
  initialOwnerMnemonic: string = "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about",
  initialExpiresInDays: number = 180,
  initialPermissions: string = "clob::submit_order,clob::cancel_order",
  initialAgentName: string = "BrowserUserAgent",
) {
  const [ownerMnemonic, setOwnerMnemonic] =
    useState<string>(initialOwnerMnemonic);
  const [ownerAddress, setOwnerAddress] = useState<string>("");
  const [agentAddress, setAgentAddress] = useState<string>("");
  const [agentPrivateKey, setAgentPrivateKey] = useState<string>("");
  const [expiresInDays, setExpiresInDays] =
    useState<number>(initialExpiresInDays);
  const [permissions, setPermissions] = useState<string>(initialPermissions);
  const [agentName, setAgentName] = useState<string>(initialAgentName);
  const [nonce, setNonce] = useState<string>(Date.now().toString());
  const [result, setResult] = useState<AgentDelegationResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const generateOwnerAddress = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    setLoading(true);
    try {
      const addressResult = await mormSigner.generateAddress(ownerMnemonic, 0);
      if (addressResult.success && addressResult.address) {
        setOwnerAddress(addressResult.address);
      } else {
        setResult({
          success: false,
          error: addressResult.error || "Failed to generate owner address",
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const generateAgentKeypair = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    setLoading(true);
    try {
      const keypairResult: KeypairResult = await mormSigner.generateKeypair();
      if (
        keypairResult.success &&
        keypairResult.address &&
        keypairResult.privateKey
      ) {
        setAgentAddress(keypairResult.address);
        setAgentPrivateKey(keypairResult.privateKey);
      } else {
        setResult({
          success: false,
          error: keypairResult.error || "Failed to generate agent keypair",
        });
      }
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  const prepareAgentSign = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner and agent addresses are required",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      const result = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      setResult(result);
    } catch (error) {
      setResult({
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Wagmi hooks for all wallet support (MetaMask, Phantom, OneKey)
  const { address: connectedAddress, isConnected, connector } = useAccount();
  const { signTypedDataAsync, isPending: isSigning } = useSignTypedData();

  // Helper to get the connected wallet type
  const getConnectedWalletType = (): SupportedWallet | null => {
    if (!isConnected || !connector) return null;

    const detected = detectSupportedWallet();
    if (isSupportedWallet(detected)) {
      return detected;
    }

    // Fallback: check connector name
    const connectorName = connector.name?.toLowerCase() || "";
    if (connectorName.includes("phantom")) return "phantom";
    if (connectorName.includes("onekey")) return "onekey";
    if (connectorName.includes("metamask")) return "metamask";

    return null;
  };

  const signAgentApproval = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your MetaMask wallet first",
      });
      return;
    }

    // Verify it's actually MetaMask
    const walletType = getConnectedWalletType();
    if (walletType !== "metamask") {
      const detected = detectSupportedWallet();
      if (detected === "metamask") {
        setResult({
          success: false,
          error:
            'MetaMask wallet detected but not connected via wagmi. Please use the "Connect MetaMask" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for MetaMask wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in MetaMask.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports MetaMask via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during MetaMask signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const signAgentApprovalWithPhantom = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your Phantom wallet first",
      });
      return;
    }

    // Verify it's actually Phantom
    const walletType = getConnectedWalletType();
    if (walletType !== "phantom") {
      const detected = detectSupportedWallet();
      if (detected === "phantom") {
        setResult({
          success: false,
          error:
            'Phantom wallet detected but not connected via wagmi. Please use the "Connect Phantom" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for Phantom wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in Phantom.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      // wagmi expects { domain, types, primaryType, message }
      const typedData = prepareResult.typedData as any;

      // Extract the EIP-712 structure
      // The typedData might be in different formats, so we handle both
      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        // Already in the correct format
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        // Try to extract from the structure
        // This handles cases where the WASM returns a different format
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports Phantom via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during Phantom signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const signAgentApprovalWithOneKey = async (): Promise<void> => {
    if (!mormSigner.isReady()) {
      setResult({
        success: false,
        error: "WASM module not ready",
      });
      return;
    }

    if (!ownerAddress || !agentAddress) {
      setResult({
        success: false,
        error: "Owner address and agent address are required",
      });
      return;
    }

    // Check if wallet is connected
    if (!isConnected || !connectedAddress) {
      setResult({
        success: false,
        error: "Please connect your OneKey wallet first",
      });
      return;
    }

    // Verify it's actually OneKey
    const walletType = getConnectedWalletType();
    if (walletType !== "onekey") {
      const detected = detectSupportedWallet();
      if (detected === "onekey") {
        setResult({
          success: false,
          error:
            'OneKey wallet detected but not connected via wagmi. Please use the "Connect OneKey" button first.',
        });
      } else {
        setResult({
          success: false,
          error:
            "This function is for OneKey wallet only. Please use the appropriate button for your wallet.",
        });
      }
      return;
    }

    // Verify the connected address matches the owner address
    if (connectedAddress.toLowerCase() !== ownerAddress.toLowerCase()) {
      setResult({
        success: false,
        error: `Connected address (${connectedAddress}) does not match owner address (${ownerAddress}). Please switch to the correct account in OneKey.`,
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const permissionsArray = permissions
        .split(",")
        .map((p) => p.trim())
        .filter((p) => p.length > 0);

      // First, prepare the typed data
      const prepareResult = await mormSigner.prepareAgentSign({
        owner: ownerAddress,
        agent: agentAddress,
        expiresInDays: expiresInDays,
        permissions: permissionsArray,
        nonce: nonce,
        agentName: agentName,
      });

      if (!prepareResult.success || !prepareResult.typedData) {
        setResult(prepareResult);
        return;
      }

      // Convert typed data to wagmi format
      const typedData = prepareResult.typedData as any;

      let domain, types, primaryType, message;

      if (typedData.domain && typedData.types && typedData.message) {
        domain = typedData.domain;
        types = typedData.types;
        primaryType =
          typedData.primaryType || typedData.primaryType || "AgentApproval";
        message = typedData.message;
      } else {
        domain = typedData.domain || {};
        types = typedData.types || typedData.Types || {};
        primaryType =
          typedData.primaryType || typedData.PrimaryType || "AgentApproval";
        message = typedData.message || typedData.Message || typedData;
      }

      // Sign with wagmi (supports OneKey via injected connector)
      const signature = await signTypedDataAsync({
        domain,
        types,
        primaryType,
        message,
      });

      setResult({
        success: true,
        typedData: prepareResult.typedData,
        signature: signature,
      });
    } catch (error: any) {
      // Handle user rejection
      if (
        error.code === 4001 ||
        error.message?.includes("User rejected") ||
        error.message?.includes("rejected")
      ) {
        setResult({
          success: false,
          error: "User rejected the signature request",
        });
      } else {
        setResult({
          success: false,
          error:
            error instanceof Error
              ? error.message
              : "Unknown error during OneKey signing",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if MetaMask is connected via wagmi (consistent with Phantom and OneKey)
  const isMetaMaskConnected =
    isConnected &&
    getConnectedWalletType() === "metamask" &&
    connectedAddress?.toLowerCase() === ownerAddress.toLowerCase();

  return {
    ownerMnemonic,
    setOwnerMnemonic,
    ownerAddress,
    setOwnerAddress,
    agentAddress,
    setAgentAddress,
    agentPrivateKey,
    setAgentPrivateKey,
    expiresInDays,
    setExpiresInDays,
    permissions,
    setPermissions,
    agentName,
    setAgentName,
    nonce,
    setNonce,
    result,
    loading: loading || isSigning,
    generateOwnerAddress,
    generateAgentKeypair,
    prepareAgentSign,
    signAgentApproval,
    signAgentApprovalWithPhantom,
    signAgentApprovalWithOneKey,
    isMetaMaskConnected,
    isPhantomConnected:
      isConnected &&
      getConnectedWalletType() === "phantom" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isOneKeyConnected:
      isConnected &&
      getConnectedWalletType() === "onekey" &&
      connectedAddress?.toLowerCase() === ownerAddress.toLowerCase(),
    isReady: mormSigner.isReady(),
  };
}
